import { Column, Index, JoinColumn, ManyToOne, OneToMany } from 'typeorm';
import type { TradeShowAttendeeRole, TradeShowTravelKind } from '../../constants/trade-shows';
import { Entity } from '../../decorators';
import { BaseEntity } from './base.entity';

/** Someone from the tenant going to a show (PD-17634), and their badge. */
@Entity('trade_show_attendee')
@Index('idx_trade_show_attendee_show', ['tradeShowId'])
export class TradeShowAttendeeEntity extends BaseEntity {
	@Column('int', { name: 'fk_trade_show__id' })
	public tradeShowId!: number;

	@Column('int', { name: 'fk_user__id' })
	public userId!: number;

	@Column('varchar', { name: 'role', length: 20, default: 'attending' })
	public role!: TradeShowAttendeeRole;

	@Column('varchar', { name: 'badgeType', length: 80, nullable: true })
	public badgeType?: string | null;

	/** Restricted: only people on the show and managers can read this. */
	@Column('varchar', { name: 'badgeConfirmation', length: 120, nullable: true })
	public badgeConfirmation?: string | null;

	@Column('date', { name: 'registeredOn', nullable: true })
	public registeredOn?: string | null;
}

/**
 * One booking for a show (PD-17634): a flight leg, a hotel, a car or something
 * else. It belongs to one or more people going, through `trade_show_travel_person`,
 * because a hotel booked by one person can hold rooms for several. A flight's
 * airline code, number and date are what link it to its live status.
 */
@Entity('trade_show_travel')
@Index('idx_trade_show_travel_show', ['tradeShowId', 'startDate'])
export class TradeShowTravelEntity extends BaseEntity {
	@Column('int', { name: 'fk_trade_show__id' })
	public tradeShowId!: number;

	@Column('varchar', { name: 'kind', length: 10 })
	public kind!: TradeShowTravelKind;

	/** The airline, hotel or rental company. */
	@Column('varchar', { name: 'name', length: 255 })
	public name!: string;

	/** Departs, checks in or picks up. Times are local to where it happens. */
	@Column('date', { name: 'startDate', nullable: true })
	public startDate?: string | null;

	@Column('time', { name: 'startTime', nullable: true })
	public startTime?: string | null;

	/** Arrives, checks out or drops off. */
	@Column('date', { name: 'endDate', nullable: true })
	public endDate?: string | null;

	@Column('time', { name: 'endTime', nullable: true })
	public endTime?: string | null;

	@Column('varchar', { name: 'airlineCode', length: 3, nullable: true })
	public airlineCode?: string | null;

	@Column('varchar', { name: 'flightNumber', length: 6, nullable: true })
	public flightNumber?: string | null;

	@Column('varchar', { name: 'fromAirport', length: 3, nullable: true })
	public fromAirport?: string | null;

	@Column('varchar', { name: 'toAirport', length: 3, nullable: true })
	public toAirport?: string | null;

	@Column('varchar', { name: 'seat', length: 10, nullable: true })
	public seat?: string | null;

	@Column('varchar', { name: 'address', length: 255, nullable: true })
	public address?: string | null;

	@Column('varchar', { name: 'phone', length: 40, nullable: true })
	public phone?: string | null;

	@Column('varchar', { name: 'websiteUrl', length: 500, nullable: true })
	public websiteUrl?: string | null;

	@Column('decimal', { name: 'ratePerNight', precision: 10, scale: 2, nullable: true })
	public ratePerNight?: string | null;

	/** In the show's hotel block. */
	@Column('boolean', { name: 'inBlock', default: 0 })
	public inBlock!: boolean;

	/** Restricted: only people on the show and managers can read this. */
	@Column('varchar', { name: 'confirmation', length: 120, nullable: true })
	public confirmation?: string | null;

	/** What the whole booking cost. Each person's share is on `trade_show_travel_person`. */
	@Column('decimal', { name: 'amount', precision: 10, scale: 2, nullable: true })
	public amount?: string | null;

	@Column('text', { name: 'notes', nullable: true })
	public notes?: string | null;

	@Column('int', { name: 'fk_user__id_booked_by', nullable: true })
	public bookedByUserId?: number | null;

	@OneToMany(() => TradeShowTravelPersonEntity, (person) => person.travel)
	public people?: Array<TradeShowTravelPersonEntity>;
}

/** A person's part of a booking: their room's dates and their share of the cost. */
@Entity('trade_show_travel_person')
@Index('idx_trade_show_travel_person_travel', ['travelId'])
@Index('idx_trade_show_travel_person_attendee', ['attendeeId'])
export class TradeShowTravelPersonEntity extends BaseEntity {
	// Unsigned to match _id: a join column declared by hand does not inherit it from the relation, and MySQL refuses the foreign key otherwise.
	@Column('int', { name: 'fk_trade_show_travel__id', unsigned: true })
	public travelId!: number;

	@ManyToOne(() => TradeShowTravelEntity, (travel) => travel.people)
	@JoinColumn({ name: 'fk_trade_show_travel__id' })
	public travel?: TradeShowTravelEntity;

	@Column('int', { name: 'fk_trade_show_attendee__id' })
	public attendeeId!: number;

	@Column('date', { name: 'startDate', nullable: true })
	public startDate?: string | null;

	@Column('date', { name: 'endDate', nullable: true })
	public endDate?: string | null;

	@Column('decimal', { name: 'amount', precision: 10, scale: 2, nullable: true })
	public amount?: string | null;
}
